import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { Form, Row, Col, notification, Divider, Spin, Space } from 'antd'
import { LoginForm, ConfirmPasswordForm, EmailConfirmForm } from 'modules/login/components'
import { Button } from 'modules/core/components'
import { useTranslation } from 'react-i18next'
import { AssociatedAccountModal, CodeModal, LoginOtherContainer } from './components'
import { tokenutils } from 'modules/login/actions'
import {
  /*GA,*/ generateMessageError,
  renderLoginAndResetFormTitle,
  renderLoginWelcome,
} from 'modules/core/utils'
import { onSubmitChange, onSubmitLogin } from './utils'
import login from 'modules/login'

import './Login.scss'
import { useFeatureValue } from '@growthbook/growthbook-react'

const Login = ({
  history,
  onLogin,
  onLogout,
  logout,
  whoAmI,
  confirmPassword,
  loggedUser: { pkid },
  fetchLoginOtherAccounts,
  azureCallback,
  sendEmailSelfResetPassword,
  setAuthenticatedTrue,
}) => {
  const tenantIdMicrosoftLogin = useFeatureValue('enable-miscrosoft-login', { tenantId: null })
  const dominioMicrosoftLogin = useFeatureValue('dominio-microsoft-login', {
    cliente: null,
    tenantId: null,
  })
  const [isLogging, setIsLogging] = useState(false)
  const [showConfirmPasswordForm, setShowConfirmPasswordForm] = useState(false)
  const [showEmailConfirmForm, setShowEmailConfirmForm] = useState(false)
  const [isLoggingOther, setIsLoggingOther] = useState(false)
  const [showAccountsModal, setShowAccountsModal] = useState(false)
  const [showQrModal, setShowQrModal] = useState(false)
  const [form] = Form.useForm()
  const { t } = useTranslation()

  var queryString = window.location.search
  var urlParams = new URLSearchParams(queryString)
  var code = urlParams.get('code')
  var error = urlParams.get('error')
  var state = urlParams.get('state')
  var tenantId = urlParams.get('tenant_id')

  const domain = window.location.host
  const subdomain = domain.split('.')[0]

  const getCallbackAction = (codeParam, stateParam, errorParam) => {
    return () => azureCallback({ code: codeParam, state: stateParam, error: errorParam })
  }

  useEffect(() => {
    return () => {
      setShowEmailConfirmForm(false)
      setShowConfirmPasswordForm(false)
      setIsLogging(false)
      setIsLoggingOther(false)
      setShowAccountsModal(false)
    }
  }, [])

  useEffect(() => {
    if (code || state || error) {
      setIsLoggingOther(true)
      const actionAux = getCallbackAction(code, state, error)
      whoAmI().then((resp) => {
        if (resp.error.response.status === 401) {
          tokenutils.removeToken()
        }
        actionAux()
          .then((response) => {
            console.log('🔍 Debug - SSO login successful, saving tokens')
            tokenutils.saveTokens(response.payload.data)
            // Obtener datos del usuario primero, luego establecer autenticación
            Promise.all([
              whoAmI().catch((error) => {
                console.error('🔍 Debug - whoAmI error:', error)
                return null
              }),
              fetchLoginOtherAccounts(),
            ])
              .then(([whoAmIResponse, responseAccount]) => {
                console.log('🔍 Debug - whoAmI response:', whoAmIResponse)
                console.log('🔍 Debug - Associated accounts:', responseAccount.payload.data.length)

                // Establecer autenticación después de que whoAmI termine
                setAuthenticatedTrue()
                console.log('🔍 Debug - Setting isAuthenticated to true after whoAmI')

                if (responseAccount.payload.data.length > 1) {
                  console.log('🔍 Debug - Multiple accounts, showing modal')
                  setShowAccountsModal(true)
                } else {
                  // Para SSO (Google/Azure), no mostrar modal de código QR automáticamente
                  // Solo redirigir al usuario a la aplicación
                  console.log('🔍 Debug - Single account, redirecting to /')
                  setIsLoggingOther(false)
                  console.log('🔍 Debug - isLoggingOther:', isLoggingOther)
                  // Pequeño delay para asegurar que el estado se actualice
                  setTimeout(() => {
                    history.push('/')
                    console.log('🔍 Debug - Redirect executed')
                  }, 100)
                }
              })
              .catch((error) => {
                console.error('🔍 Debug - Error in Promise.all:', error)
                // Si hay error, intentar redirigir de todas formas
                setIsLoggingOther(false)
                history.push('/')
              })
          })
          .catch((error) => {
            const isTenantNotAuthorized =
              error?.response?.data?.code === 'tenant_not_authorized'
            notification.error({
              message: t(
                isTenantNotAuthorized
                  ? 'LOGIN_TENANT_NOT_AUTHORIZED_FEEDBACK_TITLE'
                  : 'LOGIN_USER_NOT_FOUND_FEEDBACK_TITLE',
              ),
              description: generateMessageError(
                error,
                isTenantNotAuthorized
                  ? 'LOGIN_TENANT_NOT_AUTHORIZED_FEEDBACK_DESCRIPTION'
                  : 'LOGIN_USER_NOT_FOUND_FEEDBACK_DESCRIPTION',
              ),
              duration: 0,
            })
            setIsLoggingOther(false)
          })
      })
    }
    // eslint-disable-next-line
  }, [azureCallback, code, error, state])

  const onConfirm = () => {
    form.resetFields()
    setShowConfirmPasswordForm(true)
    setIsLogging(false)
  }

  const renderConfirmOrLoginForm = () => (
    <>
      {renderLoginAndResetFormTitle({
        title: showConfirmPasswordForm ? 'LOGIN_FORM_TITLE_CREATE_PASSWORD' : 'LOGIN_FORM_TITLE',
        level: 6,
        align: 'center',
      })}
      {showConfirmPasswordForm ? (
        <ConfirmPasswordForm
          form={form}
          onFinish={(values) =>
            onSubmitChange({
              setIsLoading: setIsLogging,
              confirmPassword: () => confirmPassword(pkid, values),
              onLogout: () => logout(),
              onClose: () => {
                setIsLogging(false)
                setShowConfirmPasswordForm(false)
                form.resetFields()
              },
              onRedirect: () => history.push('/login'),
            })
          }
          loading={isLogging}
        />
      ) : (
        <LoginForm
          form={form}
          onFinish={(values) =>
            onSubmitLogin({
              setIsLoading: setIsLogging,
              onLogin: () => onLogin({ email: values.email, password: values.password }),
              whoAmI,
              onConfirm,
              onRedirect: () => history.push('/login'),
              onShowCode: () => setShowQrModal(true),
              email: values.email,
              sendEmailSelfResetPassword: () => sendEmailSelfResetPassword({ email: values.email }),
            })
          }
          loading={isLogging}
        />
      )}
    </>
  )

  const renderEmailConfirmForm = () => (
    <>
      {renderLoginAndResetFormTitle({
        title: 'LOGIN_FORM_RESET_PASSWORD_TITLE',
        level: 6,
        align: 'center',
      })}
      <EmailConfirmForm />
    </>
  )

  const renderForgotPasswordButton = () => (
    <div className="forgot-password-container">
      <Button.Link
        onClick={() => setShowEmailConfirmForm(!showEmailConfirmForm)}
        title={
          !showEmailConfirmForm ? 'LOGIN_FORGOT_PASSWORD_ACTION' : 'LOGIN_GO_BACK_SESSION_ACTION'
        }
      />
    </div>
  )

  return (
    <>
      <Spin spinning={isLoggingOther}>
        <div className="plika-login-container">
          <Row>
            <Col span={10} offset={1} className="brand-hero">
              {renderLoginWelcome()}
            </Col>
            <Col
              span={10}
              offset={3}
              className={`login-form-container ${
                tenantIdMicrosoftLogin.tenantId === tenantId ||
                dominioMicrosoftLogin.cliente === subdomain
                  ? 'footer-layout'
                  : ''
              }`}
            >
              <div className={showConfirmPasswordForm ? 'signin-sector' : 'login-sector'}>
                {(tenantIdMicrosoftLogin.tenantId !== tenantId &&
                  dominioMicrosoftLogin.cliente !== subdomain) ||
                (tenantIdMicrosoftLogin.tenantId === null && tenantId === null) ? (
                  <>
                    {!showEmailConfirmForm ? renderConfirmOrLoginForm() : renderEmailConfirmForm()}
                    {!showConfirmPasswordForm && (
                      <>
                        {renderForgotPasswordButton()}
                        <Divider plain>{t('LABEL_OR')}</Divider>
                        <LoginOtherContainer
                          tenantIdMicrosoftLogin={tenantIdMicrosoftLogin.tenantId}
                          dominioMicrosoftLogin={dominioMicrosoftLogin.cliente !== subdomain}
                          tenantIdByDominio={dominioMicrosoftLogin.tenantId}
                        />
                        <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                          <Button.Link
                            title="SECURITY_POLICY_TERMS_BUTTON_TITLE"
                            onClick={() => window.open('/politicayterminos', '_blank')}
                            style={{ marginTop: 15 }}
                          />
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <Space direction="vertical" style={{ width: '100%' }} size="large">
                      {renderLoginAndResetFormTitle({
                        title: 'LOGIN_FORM_TITLE',
                        level: 6,
                        align: 'center',
                      })}
                      <LoginOtherContainer
                        tenantIdMicrosoftLogin={tenantIdMicrosoftLogin.tenantId}
                        dominioMicrosoftLogin={dominioMicrosoftLogin.cliente !== subdomain}
                        tenantIdByDominio={dominioMicrosoftLogin.tenantId}
                      />
                    </Space>
                    <div
                      className="bottom-block"
                      style={{ display: 'flex', justifyContent: 'center', width: '100%' }}
                    >
                      <Button.Link
                        title="SECURITY_POLICY_TERMS_BUTTON_TITLE"
                        onClick={() => window.open('/politicayterminos', '_blank')}
                        style={{ marginTop: 15 }}
                      />
                    </div>
                  </>
                )}
              </div>
            </Col>
          </Row>
        </div>
      </Spin>
      <AssociatedAccountModal
        visible={showAccountsModal}
        onCancel={() => {
          setShowAccountsModal(false)
          setIsLoggingOther(false)
        }}
      />
      <CodeModal visible={showQrModal} onCancel={() => setShowQrModal(false)} />
    </>
  )
}

const mapStateToProps = (state) => ({
  loggedUser: login.selectors.getWhoAmI(state),
})

const mapDispatchToProps = {
  onLogin: login.actions.onLogin,
  onLogout: login.actions.onLogout,
  logout: login.actions.logout,
  whoAmI: login.actions.whoAmI,
  confirmPassword: login.actions.confirmPassword,
  fetchLoginOtherAccounts: login.actions.fetchLoginOtherAccounts,
  azureCallback: login.actions.azureCallback,
  sendEmailSelfResetPassword: login.actions.sendEmailSelfResetPassword,
  setAuthenticatedTrue: login.actions.setAuthenticatedTrue,
}

export default connect(mapStateToProps, mapDispatchToProps)(Login)
